import { msg } from "@lingui/core/macro";
import { createEffect, For, Show, untrack } from "solid-js";
import { Dialog, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { useDrizzle } from "@/hooks/use-drizzle";
import {
  cn,
  DEFAULT_LIST_COLOR_HEX,
  DEFAULT_LIST_ICON,
  deleteListOptimisticMutation,
  getListFormActors,
  getThemesQuery,
  listFormMachine,
  queryKeys,
  restoreListMutation,
} from "@listto/shared";
import { fromActorRef, useActorRef } from "@fleet/state-machines/solid";
import { Button } from "@/components/ui/button";
import { Label, Switch, TextField } from "@fleet/ui";
import { useSession } from "@/lib/auth";
import { Palette, Trash2 } from "@fleet/icons";
import { ConfirmDialog } from "@/components/ui/confirm-dialog";
import { ColorPicker, IconPicker } from "./list-pickers";
import { createPowerSyncQuery } from "@listto/shared/solid";
import { usePowerSyncContext } from "@/providers/power-sync-context";
import { useI18n } from "@/providers/i18n-provider";
import { useUndo } from "@/lib/undo";

interface ListDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  list?: {
    id: string;
    name: string | null;
    color: string | null;
    icon: string | null;
    theme_id: string | null;
    is_blur: number | null;
  };
  groupId?: string;
}

export function ListDialog(props: ListDialogProps) {
  const { t } = useI18n();
  const { registerUndo } = useUndo();
  const { db } = usePowerSyncContext();
  const drizzle = useDrizzle();
  const session = useSession();

  const actorRef = useActorRef(
    untrack(() =>
      listFormMachine.provide({
        actors: getListFormActors({
          // CC-8: pass ACCESSORS, not snapshots. This dialog mounts at boot when
          // `drizzle()` is still null and the visitor's `userId` unresolved; the
          // actors must read the CURRENT db/userId when SAVE runs, not the (often
          // null) values captured here at mount — otherwise "create list" as a
          // visitor throws and the dialog never closes.
          db: () => drizzle(),
          userId: () => session.userId(),
        }),
      }),
    ),
    {
      input: untrack(() => ({
        listId: props.list?.id,
        groupId: props.groupId,
        initialData: props.list
          ? {
              name: props.list.name ?? "",
              color: props.list.color ?? DEFAULT_LIST_COLOR_HEX,
              icon: props.list.icon ?? DEFAULT_LIST_ICON,
              themeId: props.list.theme_id ?? null,
              isBlur: props.list.is_blur !== 0,
            }
          : undefined,
      })),
    },
  );

  const state = fromActorRef(actorRef);

  createEffect(() => {
    if (props.open) {
      if (props.list) {
        actorRef.send({
          type: "SYNC_DATA",
          data: {
            listId: props.list.id,
            name: props.list.name ?? "",
            color: props.list.color ?? DEFAULT_LIST_COLOR_HEX,
            icon: props.list.icon ?? DEFAULT_LIST_ICON,
            themeId: props.list.theme_id ?? null,
            isBlur: props.list.is_blur !== 0,
          },
        });
      } else {
        actorRef.send({
          type: "SYNC_DATA",
          data: {
            listId: undefined,
            name: "",
            color: DEFAULT_LIST_COLOR_HEX,
            icon: DEFAULT_LIST_ICON,
            themeId: null,
            isBlur: true,
          },
        });
      }
    }
  });

  // Closes the dialog upon ENTERING `success` (edge), with closure outside
  // reactive scope. `onOpenChange(false)` navigates (the dialog open-state lives
  // in the URL since #9/#15b) and that `navigate` READS reactive router state: if
  // executed tracked, this effect subscribes to the router and every navigation
  // re-triggers it with `matches("success")` still true → synchronous infinite loop of
  // navigations (22k+ iterations measured) freezing the renderer and killing the
  // tab in production build. Family CC-16: effects tracking what
  // their own navigation writes → compare/act under `untrack`.
  let wasSuccess = false;
  createEffect(() => {
    const isSuccess = state().matches("success");
    if (isSuccess && !wasSuccess) {
      untrack(() => props.onOpenChange(false));
    }
    wasSuccess = isSuccess;
  });

  const themes = createPowerSyncQuery({
    queryKey: () => queryKeys.themes.catalog(),
    db: () => db(),
    queryFn: () => {
      const dbInstance = drizzle();
      return dbInstance ? getThemesQuery(dbInstance) : null;
    },
    enabled: () => props.open && Boolean(drizzle()),
  });

  const isEditing = () => Boolean(props.list);
  const isSaving = () => state().matches("saving");
  // Save/delete write to local DB (drizzle); disable
  // actions until it is ready so they do not fire without being able to complete.
  const isReady = () => Boolean(drizzle());

  const handleSubmit = (e: Event) => {
    e.preventDefault();
    if (!state().context.name.trim()) {
      return;
    }
    actorRef.send({ type: "SAVE" });
  };

  const handleDelete = async () => {
    const dbInstance = drizzle();
    if (!dbInstance || !props.list) {
      return;
    }

    try {
      const listId = props.list.id;
      await deleteListOptimisticMutation(dbInstance, listId);
      registerUndo(t(msg`List moved to trash`), () => restoreListMutation(dbInstance, listId));
      props.onOpenChange(false);
    } catch (err) {
      console.error("Error deleting list:", err);
    }
  };

  return (
    <Dialog
      open={props.open}
      onOpenChange={props.onOpenChange}
      contentClass="max-w-md p-0 flex flex-col"
    >
      <DialogHeader size="md">
        <DialogTitle class="text-xl font-bold">
          {isEditing() ? t(msg`Edit list`) : t(msg`New list`)}
        </DialogTitle>
      </DialogHeader>

      <form
        onSubmit={(e) => {
          handleSubmit(e);
        }}
        class="flex flex-col max-h-[70vh] overflow-hidden"
      >
        <div class="flex-1 overflow-y-auto p-6 space-y-8 scrollbar-thin">
          {/* Preview + Name */}
          <div class="flex items-center gap-4">
            <div
              class="h-16 w-16 rounded-3xl flex items-center justify-center text-3xl shadow-lg transition-all"
              style={{ "background-color": state().context.color }}
            >
              {state().context.icon}
            </div>
            <div class="flex-1 space-y-2">
              <Label for="list-name" class="text-xs uppercase tracking-wider font-bold opacity-50">
                {t(msg`List name`)}
              </Label>
              <TextField
                id="list-name"
                value={state().context.name}
                onInput={(e) => {
                  actorRef.send({ type: "NAME_CHANGED", name: e.currentTarget.value });
                }}
                placeholder={t(msg`e.g. Shopping, Work...`)}
                autofocus
                class="bg-transparent border-none p-0 text-xl font-bold focus-visible:ring-0"
              />
            </div>
          </div>

          {/* Icon Picker */}
          <div class="space-y-3">
            <Label class="text-xs uppercase tracking-wider font-bold opacity-50">
              {t(msg`Icon`)}
            </Label>
            <IconPicker
              selectedIcon={state().context.icon}
              onSelect={(icon) => {
                actorRef.send({ type: "ICON_CHANGED", icon });
              }}
              color={state().context.color}
            />
          </div>

          {/* Color Picker */}
          <div class="space-y-3">
            <Label class="text-xs uppercase tracking-wider font-bold opacity-50">
              {t(msg`Color`)}
            </Label>
            <ColorPicker
              selectedColor={state().context.color}
              onSelect={(color) => {
                actorRef.send({ type: "COLOR_CHANGED", color });
              }}
            />
          </div>

          {/* Theme Selector */}
          <Show when={themes.data && themes.data.length > 0}>
            <div class="space-y-3">
              <div class="flex items-center justify-between">
                <Label class="text-xs uppercase tracking-wider font-bold opacity-50">
                  {t(msg`List Theme`)}
                </Label>
                <Show when={state().context.themeId}>
                  <div class="flex items-center gap-2">
                    <Label class="text-xs font-medium opacity-50">{t(msg`Blur Effect`)}</Label>
                    <Switch
                      checked={state().context.isBlur}
                      onCheckedChange={(isBlur) => {
                        actorRef.send({ type: "BLUR_CHANGED", isBlur });
                      }}
                    />
                  </div>
                </Show>
              </div>
              <div class="grid grid-cols-4 gap-3 sm:grid-cols-5">
                {/* Default Theme */}
                <button
                  type="button"
                  onClick={() => {
                    actorRef.send({ type: "THEME_CHANGED", themeId: null });
                  }}
                  class={cn(
                    "aspect-square w-full rounded-2xl border-2 flex flex-col items-center justify-center gap-1 transition-all cursor-pointer",
                    state().context.themeId === null
                      ? "border-primary bg-primary/5"
                      : "border-border/50 hover:border-border",
                  )}
                >
                  <Palette class="h-5 w-5 opacity-50" />
                  <span class="text-[10px] font-bold">{t(msg`Default`)}</span>
                </button>

                <For each={themes.data}>
                  {(theme) => (
                    <button
                      type="button"
                      onClick={() => {
                        actorRef.send({ type: "THEME_CHANGED", themeId: theme.id });
                      }}
                      class={cn(
                        "aspect-square w-full rounded-2xl border-2 overflow-hidden relative flex flex-col items-center justify-center gap-1 transition-all cursor-pointer",
                        state().context.themeId === theme.id
                          ? "border-primary"
                          : "border-border/50 hover:border-border",
                      )}
                      style={{ "background-color": theme.background ?? "transparent" }}
                    >
                      <div
                        class="h-4 w-4 rounded-full mb-1"
                        style={{ "background-color": theme.accent ?? "var(--primary)" }}
                      />
                      <span
                        class="text-[10px] font-bold px-1 text-center truncate w-full"
                        style={{ color: theme.is_dark ? "#fafafa" : "#1a1625" }}
                      >
                        {theme.name}
                      </span>
                    </button>
                  )}
                </For>
              </div>
            </div>
          </Show>
        </div>

        <div class="p-6 border-t border-border/10 bg-muted/20 flex items-center justify-between">
          <Show when={isEditing()}>
            <ConfirmDialog
              title={t(msg`Delete list?`)}
              description={t(
                msg`The list and its tasks will be moved to the trash. You can restore them from there.`,
              )}
              confirmLabel={t(msg`Delete`)}
              variant="destructive"
              onConfirm={handleDelete}
              renderTrigger={(open) => (
                <Button
                  type="button"
                  variant="ghost"
                  class="text-destructive hover:bg-destructive/10 h-11 rounded-xl px-4"
                  disabled={!isReady()}
                  onClick={open}
                >
                  <Trash2 class="h-4 w-4 mr-2" />
                  {t(msg`Delete`)}
                </Button>
              )}
            />
          </Show>
          <div class="flex items-center gap-3 ml-auto">
            <Button
              type="button"
              variant="ghost"
              class="h-11 px-6 rounded-xl font-bold"
              onClick={() => {
                props.onOpenChange(false);
              }}
            >
              {t(msg`Cancel`)}
            </Button>
            <Button
              type="submit"
              disabled={isSaving() || !state().context.name.trim() || !isReady()}
              class="h-11 px-8 font-bold rounded-xl"
            >
              {(() => {
                if (isSaving()) {
                  return t(msg`Saving...`);
                }
                if (isEditing()) {
                  return t(msg`Save changes`);
                }
                return t(msg`Create list`);
              })()}
            </Button>
          </div>
        </div>
      </form>
    </Dialog>
  );
}
